// src/pages/LexAssistPage.js — exact HTML .ai-mob design
import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';

const SUGGESTIONS = [
  'What are essential ingredients of culpable homicide under s.299?',
  'Explain bail provisions under s.437 CrPC in detail',
  'What is the scope of Article 21 right to life?',
  'Explain res gestae under s.6 Evidence Act',
  'Difference between murder and culpable homicide',
  'What constitutes public nuisance under s.268 IPC?',
];

const LexAssistPage = ({ onNav }) => {
  const { addAnnotation } = useApp();
  const [messages, setMessages] = useState([
    { id:'m0', role:'ai', text:'Ask me anything about AIR\'s commentaries and bare acts. I search only the published corpus.', sources:null }
  ]);
  const [input, setInput]   = useState('');
  const [typing, setTyping] = useState(false);
  const [copied, setCopied] = useState(null);
  const [toast, setToast]   = useState(null);
  const bottomRef           = useRef(null);
  const inputRef            = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages, typing]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2000); };

  const handleSend = () => {
    if (!input.trim() || typing) return;
    const q   = input.trim();
    const usr = { id:`m${Date.now()}`, role:'user', text:q, sources:null };
    setInput('');
    setMessages(prev => [...prev, usr]);
    setTyping(true);

    setTimeout(() => {
      const ai = {
        id: `m${Date.now()+1}`, role:'ai',
        text: `Under AIR's published commentaries, **"${q}"** is addressed as follows:\n\nThe relevant provisions and judicial interpretations are documented across multiple AIR Commentaries. The Supreme Court has laid down clear principles which have been followed by High Courts across India.\n\nThe standard test as established in leading judgments requires consideration of both the intent and the surrounding circumstances.`,
        sources: [
          { title:'AIR Commentary — relevant section and chapter' },
          { title:'Related case law citations — AIR database' },
        ],
        chapter: q,
      };
      setMessages(prev => [...prev, ai]);
      setTyping(false);
    }, 1600 + Math.random() * 600);
  };

  const handleCopy = (msg) => {
    const text = msg.text.replace(/\*\*/g,'');
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(msg.id);
    setTimeout(() => setCopied(null), 2000);
    showToast('Copied ✓');
  };

  const handleAddToNotebook = async (msg) => {
    await addAnnotation({
      type:'note', color:'blue',
      bookId: 0, bookName:'LexAssist AI',
      chapterId:null, chapterTitle:'AI Research',
      selectedText: msg.text.replace(/\*\*/g,'').substring(0,200),
      note:`Query: ${messages[messages.indexOf(msg)-1]?.text || ''}`,
      tags:['lexassist','ai'],
    });
    showToast('Added to Notebooks ✓');
  };

  return (
    <div className="flex flex-col h-full" style={{ background:'var(--lavender-pale)', fontFamily:'Nunito,system-ui,sans-serif' }}>

      {/* ── Header (exact .ai-mob-hdr) ── */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
        <div className="rounded-full flex-shrink-0"
          style={{ width:26, height:26, background:'linear-gradient(135deg,var(--lavender),#B8A8D8)' }}/>
        <div className="flex-1">
          <div className="font-bold" style={{ fontSize:13, color:'var(--ink)', fontFamily:'Nunito,sans-serif' }}>LexAssist AI</div>
          <div style={{ fontSize:9, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>AIR Commentary corpus only · Phase 3</div>
        </div>
        {onNav && (
          <button onClick={() => onNav('home')} style={{ color:'var(--ink-lt)', fontSize:16 }}>✕</button>
        )}
      </div>

      {/* ── Scope notice (exact .ai-scope) ── */}
      <div className="px-4 py-2 border-b flex-shrink-0"
        style={{ background:'var(--sage-pale)', borderColor:'#C8E8D8', fontSize:10, color:'var(--sage)', fontWeight:600, fontFamily:'Nunito,sans-serif' }}>
        🟢 Searching: AIR Commentaries + Bare Acts only · No internet
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
            <div style={{ maxWidth:'86%' }}>
              {/* Bubble (exact .ai-bub) */}
              <div className="rounded-xl px-3 py-2.5"
                style={{
                  fontSize:12, lineHeight:1.6, fontFamily:'Nunito,sans-serif',
                  background:    msg.role === 'user' ? 'var(--rose)' : 'white',
                  color:         msg.role === 'user' ? 'white'       : 'var(--ink)',
                  border:        msg.role === 'user' ? 'none'        : '1px solid var(--border)',
                  borderBottomRightRadius: msg.role === 'user' ? 2 : 10,
                  borderBottomLeftRadius:  msg.role === 'ai'   ? 2 : 10,
                }}
                dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*(.*?)\*\*/g,'<b>$1</b>').replace(/\n/g,'<br/>') }}/>

              {/* Source footnote (exact .ai-fn) */}
              {msg.sources && msg.sources.map((s, i) => (
                <div key={i} className="mt-1 rounded px-2 py-1.5 border-l-2"
                  style={{ fontSize:9, color:'var(--ink-lt)', background:'var(--lavender-pale)', borderColor:'var(--lavender)', fontFamily:'Nunito,sans-serif' }}>
                  <b style={{ color:'var(--lavender)' }}>Source:</b> {s.title}
                </div>
              ))}

              {/* Actions (exact .w-ai-actions) */}
              {msg.role === 'ai' && msg.id !== 'm0' && (
                <div className="flex gap-1.5 mt-1.5">
                  <button onClick={() => handleCopy(msg)}
                    className="border rounded font-semibold cursor-pointer transition-colors"
                    style={{ background:'var(--off-white)', borderColor:'var(--border)', fontSize:9, padding:'3px 8px', color: copied===msg.id ? 'var(--sage)' : 'var(--ink-mid)', fontFamily:'Nunito,sans-serif' }}>
                    {copied===msg.id ? '✓ Copied' : '📋 Copy+Cite'}
                  </button>
                  <button onClick={() => handleAddToNotebook(msg)}
                    className="border rounded font-semibold cursor-pointer transition-colors"
                    style={{ background:'var(--off-white)', borderColor:'var(--border)', fontSize:9, padding:'3px 8px', color:'var(--ink-mid)', fontFamily:'Nunito,sans-serif' }}>
                    📝 Add to Notebook
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {typing && (
          <div className="flex items-center gap-2 animate-fade-in">
            <div className="rounded-full" style={{ width:20, height:20, background:'var(--lavender)', opacity:.5 }}/>
            <div className="bg-white rounded-xl px-3 py-2.5 border flex gap-1.5 items-center" style={{ borderColor:'var(--border)' }}>
              {[0,1,2].map(i => (
                <div key={i} className="rounded-full animate-pulse-soft"
                  style={{ width:6, height:6, background:'var(--lavender)', opacity:.6, animationDelay:`${i*0.2}s` }}/>
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* ── Suggestions ── */}
      {messages.length <= 1 && !input && (
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar flex-shrink-0">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => { setInput(s); inputRef.current?.focus(); }}
              className="flex-shrink-0 rounded-xl text-left border cursor-pointer"
              style={{
                fontSize:10, padding:'6px 10px', maxWidth:180, lineHeight:1.4,
                color:'var(--lavender)', borderColor:'rgba(155,142,196,.3)',
                background:'rgba(155,142,196,.08)', fontFamily:'Nunito,sans-serif'
              }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* ── Input (exact .ai-inp) ── */}
      <div className="flex items-end gap-2 px-4 py-3 bg-white border-t flex-shrink-0" style={{ borderColor:'var(--border)' }}>
        <textarea ref={inputRef} value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Ask about any statute or provision…"
          rows={1}
          className="flex-1 rounded-lg outline-none resize-none"
          style={{
            padding:'8px 11px', fontSize:12, fontFamily:'Nunito,sans-serif',
            background:'white', border:'1.5px solid var(--rose-lt)',
            color:'var(--ink)', maxHeight:100, minHeight:36,
            boxShadow:'0 1px 4px rgba(201,123,119,.15)',
          }}/>
        <button onClick={handleSend}
          className="flex items-center justify-center rounded-lg border-0 cursor-pointer flex-shrink-0"
          style={{ width:32, height:32, background:'var(--lavender)', color:'white', fontSize:14 }}>
          ➤
        </button>
      </div>

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-slide-up pointer-events-none">
          <div className="px-5 py-3 rounded-xl shadow-lg text-white text-xs font-bold"
            style={{ background:'var(--sage)', fontFamily:'Nunito,sans-serif' }}>{toast}</div>
        </div>
      )}
    </div>
  );
};

export default LexAssistPage;